// src/pages/AllServices/AllServices.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import { AnyService, ServiceType, ServiceStatus } from '../../types/service.type';
import ServiceCard from '../../components/shared';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import { getServiceDetailRoute } from '../../utils/serviceRouting';

const AllServices: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<ServiceType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<ServiceStatus | 'all'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Mock data - Servicios mixtos
  const [services] = useState<AnyService[]>([
    {
      id: '1',
      name: 'my-blog',
      type: 'wordpress',
      status: 'running',
      health: 'healthy',
      primaryUrl: 'https://my-blog.kubiy.cloud',
      adminUrl: 'https://my-blog.kubiy.cloud/wp-admin',
      createdAt: new Date('2024-01-15'),
      updatedAt: new Date(),
      instanceType: 'wp-medium',
      environment: 'production',
      phpVersion: 'PHP 8.2',
      wpVersion: '6.4',
      sslEnabled: true,
      config: {} as any,
    },
    {
      id: '2',
      name: 'production-db',
      type: 'database',
      status: 'running',
      health: 'healthy',
      createdAt: new Date('2024-01-10'),
      updatedAt: new Date(),
      instanceType: 'db.r5.large',
      region: 'us-east-1',
      environment: 'production',
      engine: 'PostgreSQL',
      version: '15.4',
      port: 5432,
      username: 'admin',
    },
    {
      id: '3',
      name: 'api-server',
      type: 'vm',
      status: 'running',
      health: 'healthy',
      primaryUrl: 'https://api.kubiy.cloud',
      createdAt: new Date('2024-01-05'),
      updatedAt: new Date(),
      instanceType: 't3.medium',
      region: 'us-west-2',
      environment: 'production',
      provider: 'aws',
      os: 'Ubuntu 22.04',
      ipAddress: '54.123.45.67',
    },
    {
      id: '4',
      name: 'test-wordpress',
      type: 'wordpress',
      status: 'stopped',
      health: 'unknown',
      primaryUrl: 'https://test.kubiy.cloud',
      createdAt: new Date('2024-01-12'),
      updatedAt: new Date(),
      instanceType: 'wp-small',
      environment: 'development',
      phpVersion: 'PHP 8.1',
      wpVersion: '6.3',
      sslEnabled: false,
      config: {} as any,
    },
  ]);

  const filteredServices = services.filter((service) => {
    const matchesSearch = 
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (service.primaryUrl && service.primaryUrl.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || service.type === filterType;
    const matchesStatus = filterStatus === 'all' || service.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleActionClick = (action: string, service: AnyService) => {
    console.log(`Action ${action} on service ${service.name}`);
  };

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">All Services</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Unified view of all your cloud resources
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ReloadOutlined />
              Refresh
            </button>
            <button
              onClick={() => navigate('/resources')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusOutlined />
              Create Resource
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or URL..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <FilterOutlined className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as ServiceType | 'all')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            >
              <option value="all">All Types</option>
              <option value="wordpress">WordPress</option>
              <option value="database">Database</option>
              <option value="vm">Virtual Machine</option>
            </select>
          </div>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ServiceStatus | 'all')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            <option value="all">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="deploying">Deploying</option>
            <option value="error">Error</option>
          </select>

          {/* View Mode */}
          <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm ${
                viewMode === 'grid' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                viewMode === 'list' ? 'bg-gray-100 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Services Grid/List */}
      {filteredServices.length === 0 ? (
        <EmptyState
          title="No services found"
          description={searchTerm || filterType !== 'all' || filterStatus !== 'all'
            ? "Try adjusting your search or filters to find what you're looking for."
            : "Get started by creating your first service."}
          action={!searchTerm && filterType === 'all' && filterStatus === 'all' ? {
            label: 'Create Resource',
            onClick: () => navigate('/resources')
          } : undefined}
        />
      ) : (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  variant="default"
                  onClick={() => navigate(getServiceDetailRoute((service as any).category || service.type, service.id))}
                  onActionClick={(action) => handleActionClick(action, service)}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  variant="compact"
                  onClick={() => navigate(getServiceDetailRoute((service as any).category || service.type, service.id))}
                  onActionClick={(action) => handleActionClick(action, service)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Summary Stats */}
      {filteredServices.length > 0 && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Services</div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{services.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">WordPress</div>
            <div className="text-2xl font-bold text-blue-600">
              {services.filter(s => s.type === 'wordpress').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Databases</div>
            <div className="text-2xl font-bold text-purple-600">
              {services.filter(s => s.type === 'database').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Virtual Machines</div>
            <div className="text-2xl font-bold text-green-600">
              {services.filter(s => s.type === 'vm').length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Running</div>
            <div className="text-2xl font-bold text-green-600">
              {services.filter(s => s.status === 'running').length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllServices;